import { useMemo, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { useLanguage } from '@/i18n/LanguageContext'
import { GlassCard } from '@/components/ui/GlassCard'
import { GlowButton } from '@/components/ui/GlowButton'
import { InquiryModal } from '@/components/ui/InquiryModal'
import { WD_ADDONS } from '@/lib/pricing'
import { AddonRow } from './AddonRow'
import { iconForId } from './icons'

/**
 * "Web design" tab — flat pricing for adding features to an existing site,
 * ported from `wdRecalc()`. No bundle discount and no base price: total
 * starts at 0 EUR, unlike the main calculator's 250 EUR base.
 */
export function ExistingSiteCalculator() {
  const { t } = useLanguage()
  const [activeIds, setActiveIds] = useState<Set<string>>(new Set())
  const [inquiryOpen, setInquiryOpen] = useState(false)
  const [showAll, setShowAll] = useState(false)

  function toggle(id: string) {
    setActiveIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const { oneTime, recurring, breakdown } = useMemo(() => {
    let oneTime = 0
    let recurring = 0
    const breakdown: { name: string; price: number; suffix: string }[] = []
    for (const addon of WD_ADDONS) {
      if (!activeIds.has(addon.id)) continue
      if (addon.recurring) {
        recurring += addon.price
        breakdown.push({ name: t(addon.nameKey), price: addon.price, suffix: 'EUR/mj' })
      } else {
        oneTime += addon.price
        breakdown.push({ name: t(addon.nameKey), price: addon.price, suffix: 'EUR' })
      }
    }
    return { oneTime, recurring, breakdown }
  }, [activeIds, t])

  const summaryLines = [
    t('tab-webdesign') + ':',
    ...breakdown.map((b) => `+ ${b.name}: ${b.price} ${b.suffix}`),
    `${t('bundle-total')}: ${oneTime} EUR${recurring ? ` + ${recurring} EUR/mj` : ''}`,
  ]

  return (
    <div className="flex flex-col gap-6">
      <p className="max-w-2xl text-sm leading-relaxed text-foreground-muted">
        {t('wd-intro-pre')}{' '}
        <a href="#usluge" className="font-semibold text-accent-amber-light hover:underline">
          {t('wd-intro-link')}
        </a>{' '}
        {t('wd-intro-post')}
      </p>

      <GlassCard className="flex flex-col gap-8 p-6 md:p-8">
        <div className="grid grid-cols-1 gap-gutter lg:grid-cols-12">
          {/* Single column at every breakpoint — same reasoning as WebCalculator's
              addon grid: AddonRow's row layout collides on a half-width mobile column. */}
          <div className="grid grid-cols-1 gap-3 lg:col-span-7">
            {/* Halfway show-more — same pattern as WebCalculator: first half
                of WD_ADDONS always visible, the rest behind a toggle. */}
            {WD_ADDONS.slice(0, Math.ceil(WD_ADDONS.length / 2)).map((addon) => {
              const Icon = iconForId(addon.id)
              return (
                <AddonRow
                  key={addon.id}
                  active={activeIds.has(addon.id)}
                  onToggle={() => toggle(addon.id)}
                  icon={<Icon size={16} />}
                  name={t(addon.nameKey)}
                  note={t(addon.noteKey)}
                  price={addon.price}
                  priceSuffix={addon.recurring ? `EUR ${t('addon-maint-price')}` : undefined}
                />
              )
            })}

            {showAll &&
              WD_ADDONS.slice(Math.ceil(WD_ADDONS.length / 2)).map((addon) => {
                const Icon = iconForId(addon.id)
                return (
                  <AddonRow
                    key={addon.id}
                    active={activeIds.has(addon.id)}
                    onToggle={() => toggle(addon.id)}
                    icon={<Icon size={16} />}
                    name={t(addon.nameKey)}
                    note={t(addon.noteKey)}
                    price={addon.price}
                    priceSuffix={addon.recurring ? `EUR ${t('addon-maint-price')}` : undefined}
                  />
                )
              })}

            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-white/15 py-2.5 text-sm font-semibold text-foreground-muted transition-colors hover:border-accent-fire/50 hover:text-accent-fire"
            >
              {t(showAll ? 'calc-show-less' : 'calc-show-more')}
              <ChevronDown size={16} className={showAll ? 'rotate-180' : ''} />
            </button>
          </div>

          <div className="flex flex-col justify-between gap-6 lg:col-span-5">
            <div>
              <span className="block font-mono text-xs uppercase tracking-widest text-foreground-muted">
                {t('calc-price-label')}
              </span>
              <div className="flex items-baseline gap-2">
                <span className="font-serif text-3xl font-bold text-accent-amber-light md:text-4xl">
                  {breakdown.length === 0 ? `0 EUR` : `${oneTime} EUR`}
                </span>
                {recurring > 0 && <span className="text-sm text-foreground-muted">+ {recurring} EUR/mj</span>}
              </div>
              <p className="mt-1 text-xs text-foreground-subtle">{t('wd-total-note')}</p>
              {breakdown.length === 0 && (
                <p className="mt-3 text-xs italic text-foreground-subtle">{t('calc-no-items')}</p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <GlowButton type="button" onClick={() => setInquiryOpen(true)}>
                {t('calc-cta')}
              </GlowButton>
              <button
                type="button"
                onClick={() => setActiveIds(new Set())}
                className="rounded-pill border border-border-strong px-6 py-2.5 text-sm font-semibold text-foreground-muted transition-colors hover:border-accent-fire hover:text-accent-fire"
              >
                {t('calc-reset')}
              </button>
            </div>
          </div>
        </div>
      </GlassCard>

      <InquiryModal open={inquiryOpen} onClose={() => setInquiryOpen(false)} summary={summaryLines} />
    </div>
  )
}
